import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { isAxiosError } from 'axios';
import { toast } from 'react-hot-toast';
import { DropdownEntity } from '../common';
import { useInitDiagnosis } from '../../hooks';
import { CustomerReadDTO, CustomerSitesReadDTO, UserReadDTO } from '../../generated/api-client';
import { apiClient } from '../../services/apiClient';
import { getAxiosErrorDetail } from '../../utils/axiosErrorDetail';

interface CreateDiagnosisDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (diagnosisId: string) => void;
}

type FieldErrors = Record<'customer' | 'site' | 'code' | 'responsible', string>;

const INITIAL_ERRORS: FieldErrors = {
  customer: '',
  site: '',
  code: '',
  responsible: '',
};

export const CreateDiagnosisDialog: React.FC<CreateDiagnosisDialogProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const initDiagnosis = useInitDiagnosis();
  const submitInFlightRef = React.useRef(false);
  const [selectedCustomerId, setSelectedCustomerId] = React.useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = React.useState<string | null>(null);
  const [code, setCode] = React.useState('');
  const [selectedReferentId, setSelectedReferentId] = React.useState<string | null>(null);
  const [errors, setErrors] = React.useState<FieldErrors>(INITIAL_ERRORS);

  const [selectedCustomer, setSelectedCustomer] = React.useState<CustomerReadDTO | null>(null);
  const [selectedSite, setSelectedSite] = React.useState<CustomerSitesReadDTO | null>(null);

  const isSubmitting = initDiagnosis.isLoading;

  const resetState = React.useCallback(() => {
    setSelectedCustomerId(null);
    setSelectedSiteId(null);
    setSelectedCustomer(null);
    setSelectedSite(null);
    setCode('');
    setSelectedReferentId(null);
    setErrors(INITIAL_ERRORS);
  }, []);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    resetState();
    onClose();
  };

  const validate = (): boolean => {
    const newErrors: FieldErrors = { ...INITIAL_ERRORS };
    if (!selectedCustomerId) {
      newErrors.customer = 'Seleziona un cliente';
    }
    if (!selectedSiteId) {
      newErrors.site = 'Seleziona un sito';
    }
    if (!code.trim()) {
      newErrors.code = 'Inserisci il codice della diagnosi';
    }
    if (!selectedReferentId) {
      newErrors.responsible = 'Seleziona un referente';
    }
    setErrors(newErrors);
    return Object.values(newErrors).every(value => value === '');
  };

  const handleSubmit = async () => {
    if (submitInFlightRef.current || isSubmitting) {
      return;
    }
    if (!validate()) {
      return;
    }
    if (!selectedCustomerId || !selectedSiteId || !selectedReferentId) {
      return;
    }

    submitInFlightRef.current = true;
    try {
      const diagnosis = await initDiagnosis.mutateAsync({
        customer_id: selectedCustomerId,
        site_id: selectedSiteId,
        referent_id: selectedReferentId,
        code: code.trim(),
      });

      if (!diagnosis.id) {
        throw new Error('ID diagnosi mancante');
      }

      toast.success('Diagnosi creata con successo');
      onCreated(diagnosis.id);
      resetState();
      onClose();
    } catch (error: unknown) {
      console.error('Errore nella creazione della diagnosi', error);
      const isTimeout = isAxiosError(error) && error.code === 'ECONNABORTED';
      const message = isTimeout
        ? 'La creazione sta impiegando più del previsto. Aggiorna l’elenco diagnosi prima di riprovare.'
        : getAxiosErrorDetail(error, 'Errore nella creazione della diagnosi');
      toast.error(message);
    } finally {
      submitInFlightRef.current = false;
    }
  };

  React.useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Nuova diagnosi</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Seleziona il cliente e il sito su cui creare la diagnosi. I dati verranno copiati
            automaticamente.
          </Typography>
          <TextField
            label="Codice"
            value={code}
            onChange={event => {
              setCode(event.target.value);
              if (errors.code) {
                setErrors(current => ({ ...current, code: '' }));
              }
            }}
            error={!!errors.code}
            helperText={errors.code}
            required
          />
          <DropdownEntity<CustomerReadDTO>
            label="Cliente"
            value={selectedCustomerId}
            queryKey={['customers', 'dropdown']}
            fetchEntities={() => apiClient.list_items_customers__get()}
            getOptionLabel={option => option.company_name}
            getOptionValue={option => option.id ?? ''}
            onChange={(value, option) => {
              setSelectedCustomerId(value as string);
              setSelectedCustomer(option as CustomerReadDTO);
              setSelectedSiteId(null);
              setSelectedSite(null);
              setErrors(current => ({ ...current, customer: '', site: '' }));
            }}
            error={!!errors.customer}
            helperText={errors.customer}
            placeholder="Seleziona il cliente"
          />
          <DropdownEntity<CustomerSitesReadDTO>
            label="Sito"
            value={selectedSiteId}
            queryKey={['diagnosis-customer-sites', selectedCustomerId]}
            fetchEntities={() => {
              if (!selectedCustomerId) {
                return Promise.resolve([]);
              }
              return apiClient.search_by_customer_id_customer_sites_search_by_customer_id__customer_id__get(
                selectedCustomerId
              );
            }}
            enabled={!!selectedCustomerId}
            getOptionLabel={option => option.address}
            getOptionValue={option => option.id ?? ''}
            onChange={(value, option) => {
              setSelectedSiteId(value as string);
              setSelectedSite(option as CustomerSitesReadDTO);
              setErrors(current => ({ ...current, site: '' }));
            }}
            disabled={!selectedCustomerId}
            error={!!errors.site}
            helperText={
              errors.site ||
              (!selectedCustomerId
                ? 'Seleziona prima un cliente per visualizzare i siti'
                : undefined)
            }
            placeholder="Seleziona il sito"
          />
          <DropdownEntity<UserReadDTO>
            label="Responsabile 1"
            value={selectedReferentId}
            queryKey={['users', 'dropdown']}
            fetchEntities={() => apiClient.list_items_user__get()}
            getOptionLabel={option =>
              `${option.firstname || ''} ${option.lastname || ''} (${option.email || ''})`.trim()
            }
            getOptionValue={option => option.id ?? ''}
            onChange={value => {
              setSelectedReferentId(value as string);
              if (errors.responsible) {
                setErrors(current => ({ ...current, responsible: '' }));
              }
            }}
            error={!!errors.responsible}
            helperText={errors.responsible}
            placeholder="Seleziona il responsabile 1"
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box sx={{ py: 1, pr: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Annulla
          </Button>
          <Button
            sx={{ ml: 2 }}
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {isSubmitting ? 'Creazione...' : 'Crea diagnosi'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
